import { getFingerprint } from "@Light/app/fingerprint";
import { applyFeatureFlagOverride, getOverride } from "@Light/override";
import { getCsrfCookie } from "@Light/services/apiHelpers";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { createContext, useContext } from "react";
import {
  ConfigurationResponse,
  LoginEmailTokenRequest,
  LoginResendEmailRequest,
  LoginTokenRequest,
  LoginTokenResponse,
  ShopPlan,
  ShopRequest,
  SignupRequest,
} from "./apiTypes";

export const baseQuery = fetchBaseQuery({
  baseUrl: "/api/v1",
  prepareHeaders: (headers) => {
    const csrfToken = getCsrfCookie();
    if (csrfToken) {
      headers.set("X-CSRFToken", csrfToken);
    }
    return headers;
  },
});

export const api = createApi({
  reducerPath: "api",
  baseQuery,
  tagTypes: ["Configuration"],
  endpoints: (builder) => ({
    loginEmailToken: builder.mutation<void, LoginEmailTokenRequest>({
      query: (body) => ({
        url: "/login/email-token",
        method: "POST",
        body,
      }),
    }),
    loginResendEmail: builder.mutation<void, LoginResendEmailRequest>({
      query: (body) => ({
        url: "/login/resend-email",
        method: "POST",
        body,
      }),
    }),
    loginToken: builder.mutation<LoginTokenResponse, LoginTokenRequest>({
      query: (body) => ({
        url: "/login/token",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Configuration"],
    }),
    logout: builder.mutation<void, void>({
      query: (body) => ({
        url: "/logout",
        method: "POST",
        body,
      }),
    }),

    getConfiguration: builder.query<ConfigurationResponse, void>({
      query: () => ({
        url: "/configuration",
        method: "GET",
        params: { fingerprint: getFingerprint() },
      }),
      providesTags: ["Configuration"],
      transformResponse: (response: ConfigurationResponse) => {
        const override = getOverride();
        const feature_flags = applyFeatureFlagOverride(response.feature_flags);
        if (!override) return { ...response, feature_flags };
        return {
          ...response,
          feature_flags,
          company_name: override.companyName ?? response.company_name,
          legal_entity_name: override.companyName ?? response.legal_entity_name,
          support_email: override.supportEmail ?? response.support_email,
          logo: override.logo ?? response.logo,
          circle_logo: override.icon ?? response.circle_logo,
          brand_mode: override.brandMode ?? response.brand_mode,
        };
      },
    }),

    shop: builder.mutation<ShopPlan[], ShopRequest>({
      query: (body) => ({
        url: "/shop",
        method: "POST",
        body,
      }),
    }),
    signup: builder.mutation<void, SignupRequest>({
      query: (body) => ({
        url: "/signup",
        method: "POST",
        body,
      }),
    }),
  }),
});

export const { useGetConfigurationQuery } = api;

export const APIContext = createContext(api);
export function useAPI() {
  return useContext(APIContext);
}
